



'use client'
import { dayDiff, getFormattedDate } from '@/src/utils/date/dateHelper'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { Box, useTheme } from '@mui/material'
import { useEffect, useState, useRef } from 'react'
import dayjs from 'dayjs'
import { DateUtils } from 'react-day-picker'
import '../Calendar/_calendar.scss'

const Calendar = ({
    selectedDates = [],
    className = '',
    title = '',
    roundTrip,
    onClickCalendar,
    priceData,
    activeOnward,
    activeReturn,
    isRtl,
    isMulticity,
    multiCityDestinations,
    index,
    minDate,
    fromMonth,
    toMonth,
    numberOfMonths = 2,
    ...otherProps
}: any) => {
    const theme = useTheme()

    const today = fromMonth ? fromMonth : new Date()
    toMonth = toMonth ? toMonth : new Date(new Date().setFullYear(new Date().getFullYear() + 1))
    const [selectedDate, setSelectedDate] = useState(selectedDates['from'])
    const [range, setRange] = useState(selectedDates)
    const scrollContainerRef = useRef(null)
    const currentMonthRef = useRef<null | HTMLDivElement>(null);

    const { from, to } = range
    const { fareCalendar = {}, cheapestPricesByMonth = {}, formatCurrency } = otherProps

    // Generate 13 months for mobile view
    const months = Array.from({ length: 13 }, (_, i) => {
        return dayjs(today).add(i, 'month').toDate()
    })

    const weekSort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const weekSortAr = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
    const weekDays = isRtl ? weekSortAr : weekSort
    
    useEffect(() => {
        setRange(selectedDates)
    }, [selectedDates])

    // Sync selectedDate when selectedDates['from'] changes (important for one-way trips)
    useEffect(() => {
        setSelectedDate(selectedDates['from'])
    }, [selectedDates['from']])

    // Scroll to current month on mount
    useEffect(() => {
        if (currentMonthRef.current) {
            setTimeout(() => {
                currentMonthRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
            }, 100)
        }
    }, [])

    const handleDayClick = (day: any) => {
        if (roundTrip && activeOnward) {
            if (from && to) {
                let diff = dayDiff(day, to)
                if (diff >= 1) {
                    setSelectedDate(day)
                    onClickCalendar({
                        from: day,
                        to: day,
                        enteredTo: day
                    })
                } else {
                    setSelectedDate(day)
                    onClickCalendar({
                        ...range,
                        from: day
                    })
                }
            } else if (from && isEmpty(to)) {
                setSelectedDate(day)
                onClickCalendar({
                    ...range,
                    from: day
                })
            }
        } else if (activeReturn) {
            setSelectedDate(day)
            onClickCalendar({
                ...range,
                to: day,
                enteredTo: day
            })
        } else {
            setSelectedDate(day)
            onClickCalendar({
                from: day,
                to: null,
                enteredTo: null
            })
        }
    }

    const renderDay = (day: Date, monthDate: Date) => {
        const date = day.getDate()
        const dateKey = dayjs(day).format('YYYY-MM-DD')
        const showFareCalendar = !isEmpty(fareCalendar)
        const fareData = priceData?.[dateKey] || fareCalendar?.[dateKey]
        const fare = fareData?.amount || fareData // Support both formats: {amount, fareCategory} or number
        const hasFare = fare !== undefined && fare !== null
        
        // Calculate cheapest price for this month
        const monthKey = dayjs(day).format('YYYY-MM')
        const cheapestPrice = cheapestPricesByMonth?.[monthKey]
        const isCheapest = hasFare && cheapestPrice && fare === cheapestPrice

        // Selection logic
        const isStart = roundTrip && from && DateUtils.isSameDay(day, from)
        const isEnd = roundTrip && to && DateUtils.isSameDay(day, to)
        const isSelected = (!roundTrip && selectedDate && DateUtils.isSameDay(day, selectedDate)) || isStart || isEnd
        const isInRange = roundTrip && from && to && DateUtils.isDayAfter(day, from) && DateUtils.isDayBefore(day, to)
        const isToday = DateUtils.isSameDay(day, new Date())

        // Disabled date logic
        const isDisabled =
            DateUtils.isDayBefore(day, today) ||
            (minDate &&
                (DateUtils.isDayBefore(day, minDate) || DateUtils.isSameDay(day, minDate))) ||
            (!minDate &&
                isMulticity &&
                index > 1 &&
                !isEmpty(multiCityDestinations[`depart_date_${index - 1}`]) &&
                DateUtils.isDayBefore(
                    day,
                    new Date(getFormattedDate(multiCityDestinations[`depart_date_${index - 1}`], 'MM/DD/YYYY'))
                )) ||
            (activeReturn && range.from && DateUtils.isDayBefore(day, range.from))

        // Style logic
        let bgColor = 'transparent'
        let color = theme.palette.text.primary
        let cursor = 'pointer'

        if (isDisabled) {
            color = theme.palette.text.disabled
            cursor = 'not-allowed'
        } else if (isSelected) {
            bgColor = theme.palette.primary.main
            color = '#fff'
        } else if (isInRange) {
            bgColor = 'rgba(25, 118, 210, 0.08)'
            color = theme.palette.primary.dark
        }

        return (
            <Box
                key={`${dateKey}`}
                onClick={() => !isDisabled && handleDayClick(day)}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px',
                    borderRadius: '8px',
                    cursor,
                    opacity: isDisabled ? 0.3 : 1,
                    backgroundColor: bgColor,
                    minHeight: (roundTrip || !showFareCalendar) ? '45px' : 'auto', // Reduced height when no prices shown (round trip)
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    pointerEvents: isDisabled ? 'none' : 'auto',
                }}
            >
                <Box
                    sx={{
                        fontSize: 16,
                        fontWeight: 400,
                        color,
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {date}
                </Box>
                {!roundTrip && showFareCalendar && (hasFare ? (
                    <Box
                        sx={{
                            fontSize: 9,
                            marginTop: '2px',
                            color: isSelected 
                                ? '#fff' 
                                : isCheapest 
                                    ? theme.palette.success.main 
                                    : theme.palette.text.secondary,
                            fontWeight: isCheapest ? 700 : 600,
                        }}
                    >
                        {typeof fare === 'number' 
                            ? formatCurrency ? formatCurrency(fare, true, 0) : fare.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                            : '-'}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            fontSize: 9,
                            marginTop: '2px',
                            color: theme.palette.text.secondary,
                        }}
                    >
                        -
                    </Box>
                ))}
            </Box>
        )
    }

    const renderMonth = (monthDate: Date, monthIndex: number) => {
        const isCurrentMonth = dayjs(monthDate).isSame(dayjs(), 'month')
        const year = monthDate.getFullYear()
        const month = monthDate.getMonth()
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        const firstDayOfMonth = new Date(year, month, 1).getDay()

        const MONTHS = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ]

        const days = []

        // Empty cells for days before month starts
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<Box key={`empty-${i}`} />)
        }

        // Day cells
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day)
            days.push(renderDay(date, monthDate))
        }

        return (
            <Box
                key={`${year}-${month}`}
                ref={isCurrentMonth ? currentMonthRef : null}
                sx={{ marginBottom: 2 }}
            >
                <Box sx={{ fontSize: 18, fontWeight: 600, marginBottom: 1, textAlign: 'center' }}>
                    {MONTHS[month]} {year}
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px',
                        marginTop: 1,
                    }}
                >
                    {days}
                </Box>
            </Box>
        )
    }

    return (
        <>
        <Box sx={{
                borderBottom: 2,
                borderColor: 'divider',
                mb: 2
            }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px',
                    bgcolor: 'white',
                    px: 1
                }}
            >
            {weekDays.map((day) => (
                <Box
                    key={day}
                    sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        textAlign: 'center',
                        padding: '8px 0px',
                        color: theme.palette.text.primary,
                    }}
                >
                    {day}
                </Box>
            ))}
            </Box>
        </Box>
        <Box ref={scrollContainerRef}
            sx={{
                overflowY: 'auto',
                scrollBehavior: 'smooth',
                padding: 0.5
            }}>
            {months.map((month, index) => renderMonth(month, index))}
            </Box>
        </>
    )
}

export default Calendar

